import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { Calendar, MapPin, Users, Trophy, Search, X, Map as MapIcon, List as ListIcon, UserPlus, LogIn, Loader2 } from 'lucide-react';
import { GAMES, FORMATS, getGameColor, getGameName } from '../constants/theme';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix default marker icons (Leaflet + bundlers)
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const formatDate = (d) => new Date(d).toLocaleDateString('fr-FR', {
  weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
});

// Haversine distance in km between two [lat, lng] points
const distanceKm = (lat1, lng1, lat2, lng2) => {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export default function AllTournamentsPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [city, setCity] = useState('');
  const [game, setGame] = useState('all');
  const [format, setFormat] = useState('all');
  const [view, setView] = useState('list');
  const [radius, setRadius] = useState('0');
  const [center, setCenter] = useState(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const [authPrompt, setAuthPrompt] = useState(null);
  const geoTimer = useRef(null);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API_URL}/api/public/tournaments`, { params: { limit: 200 } })
      .then((r) => setTournaments(r.data || []))
      .catch(() => setTournaments([]))
      .finally(() => setLoading(false));
  }, []);

  // Geocode the typed city (OpenStreetMap Nominatim) when a radius is active
  useEffect(() => {
    if (geoTimer.current) clearTimeout(geoTimer.current);
    if (radius === '0' || !city.trim()) { setCenter(null); setGeoLoading(false); return; }
    setGeoLoading(true);
    geoTimer.current = setTimeout(async () => {
      try {
        const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=fr&q=${encodeURIComponent(city.trim())}`, { headers: { 'Accept-Language': 'fr' } });
        const data = await r.json();
        if (data && data[0]) setCenter([parseFloat(data[0].lat), parseFloat(data[0].lon)]);
        else setCenter(null);
      } catch { setCenter(null); }
      finally { setGeoLoading(false); }
    }, 600);
    return () => clearTimeout(geoTimer.current);
  }, [city, radius]);

  // Only upcoming (never past)
  const now = Date.now();
  const upcoming = useMemo(
    () => tournaments.filter((t) => t.date && new Date(t.date).getTime() >= now - 6 * 3600 * 1000),
    [tournaments, now],
  );

  const availableFormats = useMemo(() => {
    if (game !== 'all' && FORMATS[game]) return FORMATS[game];
    return [...new Set(upcoming.map((t) => t.format).filter(Boolean))];
  }, [game, upcoming]);

  const radiusKm = parseInt(radius, 10);
  const useRadius = radiusKm > 0 && !!center && !!city.trim();

  const filtered = useMemo(() => upcoming.filter((t) => {
    if (game !== 'all' && t.game !== game) return false;
    if (format !== 'all' && t.format !== format) return false;
    if (city.trim()) {
      if (useRadius) {
        // Radius mode: keep events within N km of the geocoded city center
        if (t.venue?.latitude && t.venue?.longitude) {
          if (distanceKm(center[0], center[1], t.venue.latitude, t.venue.longitude) > radiusKm) return false;
        } else {
          // No coords -> fall back to text match on city
          const c = (t.venue?.city || '') + ' ' + (t.venue?.name || '') + ' ' + (t.venue?.address || '');
          if (!c.toLowerCase().includes(city.trim().toLowerCase())) return false;
        }
      } else {
        const c = (t.venue?.city || '') + ' ' + (t.venue?.name || '') + ' ' + (t.venue?.address || '');
        if (!c.toLowerCase().includes(city.trim().toLowerCase())) return false;
      }
    }
    return true;
  }).sort((a, b) => new Date(a.date) - new Date(b.date)), [upcoming, game, format, city, useRadius, center, radiusKm]);

  const resetFilters = () => { setCity(''); setGame('all'); setFormat('all'); setRadius('0'); setCenter(null); };
  const hasFilters = city.trim() || game !== 'all' || format !== 'all' || radius !== '0';

  const goToEvent = (t) => navigate(`/player/event/tournament/${t.id}`);
  const handleJoin = (t, e) => {
    if (e) e.stopPropagation();
    if (!isAuthenticated) { setAuthPrompt(t); return; }
    goToEvent(t);
  };

  return (
    <div className="space-y-6" data-testid="all-tournaments-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2" data-testid="all-tournaments-title">
            <Trophy className="w-6 h-6 text-[#FF6B35]" />Tournois à venir
          </h1>
          <p className="text-[#666] text-sm mt-1">{filtered.length} tournoi{filtered.length > 1 ? 's' : ''} à venir</p>
        </div>
        <div className="flex rounded-lg overflow-hidden border border-[#333] shrink-0" data-testid="view-toggle">
          <button onClick={() => setView('list')} className={`px-3 py-2 text-sm flex items-center gap-1.5 transition-colors ${view === 'list' ? 'bg-[#FF6B35] text-white' : 'bg-[#1a1a1a] text-[#888] hover:text-white'}`} data-testid="view-list-btn"><ListIcon className="w-4 h-4" />Liste</button>
          <button onClick={() => setView('map')} className={`px-3 py-2 text-sm flex items-center gap-1.5 transition-colors ${view === 'map' ? 'bg-[#FF6B35] text-white' : 'bg-[#1a1a1a] text-[#888] hover:text-white'}`} data-testid="view-map-btn"><MapIcon className="w-4 h-4" />Carte</button>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="relative">
          <Search className="w-4 h-4 text-[#666] absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            placeholder="Lieu / ville..." value={city} onChange={(e) => setCity(e.target.value)}
            className="bg-[#1a1a1a] border-[#333] text-white pl-9" data-testid="filter-city"
          />
          {geoLoading && <Loader2 className="w-4 h-4 text-[#FF6B35] absolute right-3 top-1/2 -translate-y-1/2 animate-spin" />}
        </div>
        <Select value={radius} onValueChange={setRadius}>
          <SelectTrigger className="bg-[#1a1a1a] border-[#333] text-white" data-testid="filter-radius"><SelectValue placeholder="Rayon" /></SelectTrigger>
          <SelectContent className="bg-[#1a1a1a] border-[#333]">
            <SelectItem value="0" className="text-white">Ville exacte</SelectItem>
            <SelectItem value="10" className="text-white">Dans un rayon de 10 km</SelectItem>
            <SelectItem value="25" className="text-white">Dans un rayon de 25 km</SelectItem>
            <SelectItem value="50" className="text-white">Dans un rayon de 50 km</SelectItem>
            <SelectItem value="100" className="text-white">Dans un rayon de 100 km</SelectItem>
          </SelectContent>
        </Select>
        <Select value={game} onValueChange={(v) => { setGame(v); setFormat('all'); }}>
          <SelectTrigger className="bg-[#1a1a1a] border-[#333] text-white" data-testid="filter-game"><SelectValue placeholder="Jeu" /></SelectTrigger>
          <SelectContent className="bg-[#1a1a1a] border-[#333]">
            <SelectItem value="all" className="text-white">Tous les jeux</SelectItem>
            {GAMES.map((g) => <SelectItem key={g.id} value={g.id} className="text-white">{g.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={format} onValueChange={setFormat}>
          <SelectTrigger className="bg-[#1a1a1a] border-[#333] text-white" data-testid="filter-format"><SelectValue placeholder="Format" /></SelectTrigger>
          <SelectContent className="bg-[#1a1a1a] border-[#333]">
            <SelectItem value="all" className="text-white">Tous les formats</SelectItem>
            {availableFormats.map((f) => <SelectItem key={f} value={f} className="text-white">{f}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {radius !== '0' && city.trim() && !center && !geoLoading && (
        <p className="text-xs text-[#888]" data-testid="radius-geo-warning">Ville introuvable pour le rayon — filtrage par nom de ville appliqué.</p>
      )}
      {useRadius && (
        <p className="text-xs text-[#4ECDC4]" data-testid="radius-active">Rayon actif : {radiusKm} km autour de « {city.trim()} »</p>
      )}
      {hasFilters && (
        <button onClick={resetFilters} className="text-xs text-[#FF6B35] hover:text-[#E55A25] inline-flex items-center gap-1" data-testid="reset-filters">
          <X className="w-3 h-3" />Réinitialiser les filtres
        </button>
      )}

      {/* Map view */}
      {!loading && view === 'map' && (() => {
        const geo = filtered.filter((t) => t.venue?.latitude && t.venue?.longitude);
        const center = geo.length ? [geo[0].venue.latitude, geo[0].venue.longitude] : [46.6, 2.3];
        return (
          <div className="rounded-xl overflow-hidden border border-[#333]" data-testid="tournaments-map">
            <MapContainer center={center} zoom={geo.length ? 6 : 5} style={{ height: 460, width: '100%' }} scrollWheelZoom>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
              {geo.map((t) => (
                <Marker key={t.id} position={[t.venue.latitude, t.venue.longitude]}>
                  <Popup>
                    <div style={{ minWidth: 160 }}>
                      <strong>{t.name}</strong><br />
                      <span style={{ color: getGameColor(t.game) }}>{getGameName(t.game)}</span>{t.format ? ` · ${t.format}` : ''}<br />
                      <span>{formatDate(t.date)}</span><br />
                      {t.venue?.city && <span>📍 {t.venue.city}<br /></span>}
                      <span>{t.price === 0 ? 'Gratuit' : `${t.price}€`}</span><br />
                      <button onClick={() => goToEvent(t)} style={{ color: '#FF6B35', fontWeight: 600, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>Voir la fiche →</button>{'  '}
                      <button onClick={() => handleJoin(t)} style={{ color: '#4ECDC4', fontWeight: 600, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>S'inscrire</button>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
            {geo.length === 0 && <p className="text-[#666] text-sm text-center py-3 bg-[#1a1a1a]">Aucun événement géolocalisé pour ces filtres (les événements en ligne n'apparaissent pas sur la carte)</p>}
          </div>
        );
      })()}

      {/* List */}
      {!loading && view === 'list' && (
        filtered.length === 0 ? (
          <Card className="bg-[#1a1a1a] border-[#333]"><CardContent className="p-8 text-center"><Trophy className="w-12 h-12 text-[#333] mx-auto mb-3" /><p className="text-[#666]">Aucun tournoi à venir ne correspond à ces filtres</p></CardContent></Card>
        ) : (
        <div className="grid gap-2">
          {filtered.map((t) => (
            <div key={t.id}
              onClick={() => navigate(`/player/event/tournament/${t.id}`)}
              className="flex items-center gap-3 bg-[#1a1a1a] rounded-lg px-4 py-3 border border-[#333] hover:border-[#FF6B35]/40 transition-colors cursor-pointer"
              data-testid={`all-tournament-row-${t.id}`}>
              <div className="w-1 h-12 rounded-full shrink-0" style={{ backgroundColor: getGameColor(t.game) }} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white font-semibold text-sm truncate max-w-[320px]">{t.name}</span>
                  <Badge className="text-[10px] px-1.5 py-0" style={{ backgroundColor: `${getGameColor(t.game)}20`, color: getGameColor(t.game) }}>{getGameName(t.game)}</Badge>
                  {t.format && <Badge className="bg-[#252525] text-[#888] text-[10px] px-1.5 py-0">{t.format}</Badge>}
                </div>
                <div className="flex items-center gap-3 text-xs text-[#666] mt-1 flex-wrap">
                  <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(t.date)}</span>
                  {t.venue?.city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{t.venue.city}</span>}
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" />{t.participants_count}/{t.capacity || '?'}</span>
                  <span className="font-semibold" style={{ color: t.price === 0 ? '#4CAF50' : '#FF6B35' }}>{t.price === 0 ? 'Gratuit' : `${t.price}€`}</span>
                </div>
              </div>
              <button
                onClick={(e) => handleJoin(t, e)}
                className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#FF6B35] hover:bg-[#E55A25] text-white text-xs font-semibold transition-colors"
                data-testid={`join-btn-${t.id}`}
              >
                <UserPlus className="w-3.5 h-3.5" />S'inscrire
              </button>
            </div>
          ))}
        </div>
        )
      )}
      {loading && (
        <div className="flex items-center justify-center h-48"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B35]" /></div>
      )}

      {/* Auth prompt for anonymous users trying to register */}
      <Dialog open={!!authPrompt} onOpenChange={(v) => { if (!v) setAuthPrompt(null); }}>
        <DialogContent className="bg-[#1a1a1a] border-[#333] text-white max-w-sm" data-testid="auth-prompt-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-[#FF6B35]" />Créez un compte pour vous inscrire</DialogTitle>
            <DialogDescription className="text-[#888]">
              Pour rejoindre {authPrompt ? `« ${authPrompt.name} »` : 'cet évènement'}, connectez-vous ou créez un compte Arcanapp gratuit.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 mt-2">
            <button
              onClick={() => navigate(`/login?redirect=/player/event/tournament/${authPrompt?.id}`)}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#FF6B35] hover:bg-[#E55A25] text-white font-semibold transition-colors"
              data-testid="auth-prompt-register-btn"
            >
              <UserPlus className="w-4 h-4" />Créer un compte
            </button>
            <button
              onClick={() => navigate(`/login?redirect=/player/event/tournament/${authPrompt?.id}`)}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#252525] hover:bg-[#333] text-white font-medium border border-[#333] transition-colors"
              data-testid="auth-prompt-login-btn"
            >
              <LogIn className="w-4 h-4" />J'ai déjà un compte
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
